import { Alert, Button, Icon, SearchInput, Text } from "@assetwatch/ui";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { useId, useState } from "react";
import {
  type AssetPickerNode,
  buildAssetPickerTree,
  getAncestorPath,
} from "../../../lib/assetPickerTree";
import type { AssetId } from "../../../lib/workIds";
import type { AssetPickerOption } from "../../../queries/useAssetPickerQuery";
import { useAssetPickerQuery } from "../../../queries/useAssetPickerQuery";
import { RequestFieldLabel } from "./RequestFieldLabel";

type AssetKind = "ASSET" | "LOCATION";

type Props = {
  /** Visible field label. Defaults to the New Request wording. */
  label?: string;
  value: AssetId | null;
  /**
   * Name shown for `value` when the picker does not list it, for example a
   * saved record that is no longer offered for new selections.
   */
  fallbackName?: string | null;
  onChange: (
    assetId: AssetId | null,
    assetName: string | null,
    assetKind: AssetKind | null,
  ) => void;
};

function kindIcon(kind: AssetKind) {
  return kind === "LOCATION" ? "location-pin-solid" : "asset";
}

function ResultRow({
  ancestorPath,
  isSelected,
  onSelect,
  option,
}: {
  ancestorPath: string;
  isSelected: boolean;
  onSelect: () => void;
  option: AssetPickerOption;
}) {
  return (
    <button
      aria-selected={isSelected}
      className="flex w-full items-center gap-(--spacing-10) px-(--spacing-12) py-(--spacing-10) text-left outline-none hover:bg-surface-hover focus-visible:bg-surface-hover"
      onClick={onSelect}
      role="option"
      type="button"
    >
      <Icon name={kindIcon(option.kind)} size="sm" tone="secondary" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-(family-name:--font-family-label) text-(length:--font-size-body-medium) text-text-default">
          {option.name}
        </span>
        {ancestorPath ? (
          <span className="block truncate text-(length:--font-size-body-small) text-text-subtle">
            {ancestorPath}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function TreeRow({
  depth,
  expandedIds,
  node,
  onSelect,
  onToggle,
  selectedId,
}: {
  depth: number;
  expandedIds: ReadonlySet<AssetId>;
  node: AssetPickerNode;
  onSelect: (node: AssetPickerNode) => void;
  onToggle: (id: AssetId) => void;
  selectedId: AssetId | null;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = node.id === selectedId;

  return (
    <div>
      <div
        className="flex items-center gap-(--spacing-4)"
        style={{ paddingLeft: depth * 16 }}
      >
        {hasChildren ? (
          <button
            aria-expanded={isExpanded}
            aria-label={
              isExpanded ? `Collapse ${node.name}` : `Expand ${node.name}`
            }
            className="flex shrink-0 items-center justify-center rounded-(--radius-sm) p-(--spacing-4) outline-none hover:bg-surface-hover"
            onClick={() => onToggle(node.id)}
            type="button"
          >
            <Icon
              name={isExpanded ? "chevron-down" : "chevron-right"}
              size="xs"
              tone="secondary"
            />
          </button>
        ) : (
          <span
            aria-hidden="true"
            className="inline-block size-(--spacing-24)"
          />
        )}
        <button
          aria-selected={isSelected}
          className="flex min-w-0 flex-1 items-center gap-(--spacing-8) rounded-(--radius-sm) px-(--spacing-6) py-(--spacing-8) text-left outline-none hover:bg-surface-hover focus-visible:bg-surface-hover"
          onClick={() => onSelect(node)}
          role="option"
          type="button"
        >
          <Icon name={kindIcon(node.kind)} size="sm" tone="secondary" />
          <span className="truncate font-(family-name:--font-family-body) text-(length:--font-size-body-medium) text-text-default">
            {node.name}
          </span>
        </button>
      </div>
      {hasChildren && isExpanded
        ? node.children.map((child) => (
            <TreeRow
              depth={depth + 1}
              expandedIds={expandedIds}
              key={child.id}
              node={child}
              onSelect={onSelect}
              onToggle={onToggle}
              selectedId={selectedId}
            />
          ))
        : null}
    </div>
  );
}

/**
 * Combined Equipment/Location picker: a flat search over eligible facility
 * records in search mode, and a clickable hierarchy in browse mode — every
 * node (Equipment and Location alike) directly selectable, not
 * navigation-only. Name is the only identifying text ever displayed;
 * Number/ID is search-only (IWA-19839 AC).
 */
export function EquipmentLocationPicker({
  label = "Asset or location",
  value,
  fallbackName = null,
  onChange,
}: Props) {
  const { data, isError, isPending, isUnavailable, refetch } =
    useAssetPickerQuery();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<AssetId>>(
    new Set(),
  );
  const listboxId = useId();
  const triggerId = useId();

  // A query this role can't run would otherwise stay pending forever, so say
  // so instead of showing a loading state that never resolves. A scope that
  // is still resolving shows the loading text below instead.
  if (isUnavailable) {
    return <Alert title="Assets are unavailable." tone="negative" />;
  }
  if (isPending) return <Text tone="subtle">Loading assets...</Text>;
  if (isError) {
    return (
      <Alert
        action={
          <Button onClick={() => void refetch()} size="sm" variant="outline">
            Retry
          </Button>
        }
        title="Unable to load assets. Retry to choose an asset or location."
        tone="negative"
      />
    );
  }

  const options = data?.options ?? [];
  const selected = value
    ? (options.find((option) => option.id === value) ?? null)
    : null;
  const tree = buildAssetPickerTree(options);
  const normalizedQuery = query.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;
  const searchResults = isSearching
    ? options.filter(
        (option) =>
          option.name.toLowerCase().includes(normalizedQuery) ||
          (option.assetNumber ?? "").toLowerCase().includes(normalizedQuery) ||
          option.id.toLowerCase().includes(normalizedQuery),
      )
    : [];

  function select(id: AssetId, name: string, kind: AssetKind) {
    onChange(id, name, kind);
    setOpen(false);
    setQuery("");
  }

  function toggle(id: AssetId) {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="flex w-full flex-col gap-(--spacing-4)">
      <RequestFieldLabel htmlFor={triggerId}>{label}</RequestFieldLabel>
      <PopoverPrimitive.Root
        modal={false}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setQuery("");
        }}
        open={open}
      >
        {/* @ds-deviation: no DS select/combobox can host this searchable tree, so the popover trigger reuses the Input container tokens (height, border, hover, pressed and active outline) — add a combobox trigger primitive to the design system */}
        <PopoverPrimitive.Trigger
          render={
            <button
              aria-controls={listboxId}
              aria-expanded={open}
              className="flex h-(--spacing-40) w-full items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-solid border-input-default-outline bg-input-default-background px-(--spacing-10) text-left outline-none transition-colors duration-200 ease-in-out [&:not(:focus-visible):not([data-popup-open])]:hover:border-input-hover-outline [&:not(:focus-visible):not([data-popup-open])]:hover:bg-input-hover-background active:border-input-pressed-outline focus-visible:border-input-active-outline data-[popup-open]:border-input-active-outline"
              id={triggerId}
              role="combobox"
              type="button"
            >
              <span className="flex min-w-0 flex-1 items-center gap-(--spacing-8)">
                {selected ? (
                  <Icon
                    name={kindIcon(selected.kind)}
                    size="sm"
                    tone="secondary"
                  />
                ) : null}
                <span
                  className={
                    selected || (value && fallbackName)
                      ? "truncate text-(length:--font-size-body-medium) text-input-text-default"
                      : "truncate text-(length:--font-size-body-medium) text-input-text-placeholder"
                  }
                >
                  {selected
                    ? selected.name
                    : value && fallbackName
                      ? fallbackName
                      : "Search by name or ID..."}
                </span>
              </span>
              <Icon
                name={open ? "chevron-up" : "chevron-down"}
                size="xs"
                tone="secondary"
              />
            </button>
          }
        />
        {/*
         * Composed directly on @base-ui/react/popover instead of
         * @assetwatch/ui's shared Popover: that primitive doesn't set a
         * z-index on its Positioner, so when opened from inside a Drawer
         * (as here) it renders behind the Drawer's z-layer-dialog layer —
         * invisible, no error. z-[var(--z-layer-popover)] below fixes it
         * locally for this usage.
         */}
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Positioner
            align="start"
            className="z-[var(--z-layer-popover)]"
            side="bottom"
            sideOffset={4}
          >
            <PopoverPrimitive.Popup className="flex w-(--anchor-width) flex-col gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle bg-surface p-(--spacing-8) shadow-elevation-3 outline-none">
              <SearchInput
                aria-label="Search assets or locations"
                autoFocus
                debounceMs={0}
                onChange={setQuery}
                placeholder="Search by name or ID..."
                value={query}
              />
              <div
                className="max-h-[320px] overflow-y-auto"
                id={listboxId}
                role="listbox"
              >
                {isSearching ? (
                  searchResults.length > 0 ? (
                    searchResults.map((option) => (
                      <ResultRow
                        ancestorPath={getAncestorPath(options, option.id)}
                        isSelected={option.id === value}
                        key={option.id}
                        onSelect={() =>
                          select(option.id, option.name, option.kind)
                        }
                        option={option}
                      />
                    ))
                  ) : (
                    <div className="px-(--spacing-16) py-(--spacing-20) text-center">
                      <Text tone="subtle">
                        No assets or locations match &quot;{query}&quot;
                      </Text>
                    </div>
                  )
                ) : tree.length > 0 ? (
                  tree.map((node) => (
                    <TreeRow
                      depth={0}
                      expandedIds={expandedIds}
                      key={node.id}
                      node={node}
                      onSelect={(selectedNode) =>
                        select(
                          selectedNode.id,
                          selectedNode.name,
                          selectedNode.kind,
                        )
                      }
                      onToggle={toggle}
                      selectedId={value}
                    />
                  ))
                ) : (
                  <div className="px-(--spacing-16) py-(--spacing-20) text-center">
                    <Text tone="subtle">No assets or locations available.</Text>
                  </div>
                )}
              </div>
            </PopoverPrimitive.Popup>
          </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
}
